import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { useParams, useNavigate } from 'react-router-dom';
import { Form } from '@unform/web';

import Api, { exceptionNotificationAPI } from '../../services/api';
import getValidationErrors from '../../utils/getValidationErrors';
import { formSelectStyles, formSelectControlFullWidth } from '../../components/FormInput/formSelectStyles';
import { Container } from '../../components/FormCrud/styles';
import Button from '../../components/Button';
import FormInput from '../../components/FormInput';
import CancelButton from '../../components/CancelButton';
import RichTextEditor from '../../components/Paragraph';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';

const selectWrapperStyle = { width: '100%' };
const selectStylesBase = {
    ...formSelectStyles,
    control: (base, state) => ({ ...formSelectControlFullWidth(base, state) }),
};

const ROLES_OPTIONS = [
    { value: 'RoleInfoHolder',             label: 'RoleInfoHolder' },
    { value: 'RoleApprover',               label: 'RoleApprover' },
    { value: 'RoleReadOnly',               label: 'RoleReadOnly' },
    { value: 'RoleReviewer',               label: 'RoleReviewer' },
    { value: 'RoleDeploymentTrackerReviewer', label: 'RoleDeploymentTrackerReviewer' }
];

const AnnualPopupMessageForm = () => {
    const formRef = useRef(null);
    const navigate = useNavigate();
    const { id } = useParams();
    const animatedComponents = makeAnimated();

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);
    const [reportOptions, setReportOptions] = useState([]);
    const [companysOptions, setCompanysOptions] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [multipleValues, setMultipleValues] = useState([]);
    const [selectedTargetRoles, setSelectedTargetRoles] = useState([]);
    const [richTextValue, setRichTextValue] = useState('');
    const [isActive, setIsActive] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const activeOptions = [
        { value: true,  text: 'Yes' },
        { value: false, text: 'No' },
    ];

    const fields = [
        {
            label: 'Report Year',
            name: 'reportYearID',
            type: 'select',
            options: reportOptions,
        },
        {
            label: 'Title',
            name: 'title',
            type: 'input',
        },
        {
            label: 'Body',
            name: 'body',
            type: 'textareaRichText',
        },
        {
            label: 'Display Start Date',
            name: 'displayStartDate',
            type: 'date',
        },
        {
            label: 'Display End Date',
            name: 'displayEndDate',
            type: 'date',
        },
        {
            label: 'Target Roles',
            name: 'targetRoles',
            type: 'rolesMultiSelect',
            required: false,
        },
        {
            label: 'Companies to Exclude',
            name: 'annualPopupMessageCompany',
            type: 'select',
            required: false,
            options: companysOptions,
            multiple: true,
        },
        {
            label: 'Is Active',
            name: 'isActive',
            type: "check",
            disabled: false,
            required: true,
        },
    ];

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            try {
                await Promise.all([loadReportYear(), loadCompanies()]);
            } finally {
                setIsLoading(false);
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (id != null && !isLoading && data === null) {
            loadData();
        }
    }, [id, isLoading]);

    const loadReportYear = async () => {
        var userLogged = JSON.parse(localStorage.getItem('@Thrive:user'));
        Api.get(`Culture/getAllReportYearsByCulture?cultureID=${userLogged.selectedCultureID}`).then((result) => {
            if (result && result.data) {
                const options = result.data.response?.reportYears?.map(item => ({ value: item.reportYearID, text: `${item.year}` }));
    
                setReportOptions(options);
            }
    
        }).catch((error) => {
            exceptionNotificationAPI(error);
        });
    }

    const loadCompanies = async () => {
        await Api.get('Company/getAll')
            .then((result) => {
                if (result && result.data) {
                    const allCompanies = result.data.response;
                    setCompanies([
                        { value: '', label: 'Global' },
                        ...allCompanies.map(c => ({ value: c.companyID, label: c.name })),
                    ]);
                    setCompanysOptions(
                        allCompanies.map(c => ({ value: c.companyID, text: c.name }))
                    );
                }
            })
            .catch(exceptionNotificationAPI);
    };

    const loadData = async () => {
        await Api.get(`annualPopupMessage/get?id=${id}`)
            .then((result) => {
                if (result && result.data) {
                    const response = result.data.response;
                    const dataObj = {};

                    fields.forEach(f => {
                        if (f.name === 'body') {
                            dataObj[f.name] = response[f.name];
                            setRichTextValue(response[f.name] || '');
                        } else if (f.name === 'targetRoles') {
                            if (response.targetRoles) {
                                const parsed = response.targetRoles
                                    .split(';')
                                    .map(r => r.trim())
                                    .filter(Boolean)
                                    .map(r => ROLES_OPTIONS.find(o => o.value === r))
                                    .filter(Boolean);
                                setSelectedTargetRoles(parsed);
                            }
                        } else if (f.name === 'annualPopupMessageCompany' && response.annualPopupMessageCompany) {
                            setMultipleValues(
                                response.annualPopupMessageCompany.map(sc => ({
                                    value: sc.companyID,
                                    label: sc.company.name,
                                }))
                            );
                        } else if (f.type === 'check') {
                            setIsActive(!!response[f.name]);
                        } else {
                            dataObj[f.name] = response[f.name] ?? '';
                        }
                    });

                    formRef.current.setData(dataObj);
                    setData(dataObj);
                }
            })
            .catch(exceptionNotificationAPI);
    };

    const handleSubmit = async (formData) => {
        try {
            setFieldErrors({});

            const validationShape = {};
            fields.forEach(f => {
                if (f.required === false || f.type === 'textareaRichText' || f.type === 'rolesMultiSelect' || f.type === 'check' || f.multiple) return;
                validationShape[f.name] = Yup.string().required(`${f.label} is required`);
            });

            const schema = Yup.object().shape(validationShape);
            await schema.validate(formData, { abortEarly: false });

            const postObj = { annualPopupMessageID: id };

            fields.forEach(f => {
                if (f.type === 'textareaRichText' || f.type === 'rolesMultiSelect' || f.type === 'check' || f.multiple) return;

                let value = formData[f.name];
                if (f.type === 'select' && f.options?.some(o => o.value === true || o.value === false)) {
                    value = value === 'true';
                }
                postObj[f.name] = value;
            });

            postObj.body = richTextValue;
            postObj.isActive = isActive;
            postObj.targetRoles = selectedTargetRoles.map(r => r.value).join(';');

            if (multipleValues.length > 0) {
                postObj.annualPopupMessageCompany = multipleValues.map(m => ({
                    companyID: m.value || 0,
                }));
            }

            await Api.post(`annualPopupMessage/${id != null ? 'edit' : 'new'}`, postObj);
            navigate('/annualPopupMessage');
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                const errors = getValidationErrors(error);
                setFieldErrors(errors);
                formRef.current.setErrors(errors);
                return;
            }
            exceptionNotificationAPI(error);
        }
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <Container className="container">
            <div>
                <h3>{id != null ? 'Edit' : 'New'} Annual Popup Message</h3>
                <Form onSubmit={handleSubmit} ref={formRef}>
                    <table className="table table-striped">
                        <tbody>
                            {fields.map((f, i) => {
                                if (f.type === 'textareaRichText') {
                                    return (
                                        <tr key={i + 'Tr'} id={f.name + 'Tr'}>
                                            <td width="25%">
                                                <label>
                                                    {f.label}{' '}
                                                    {f.required !== false && <span className="required">*</span>}
                                                </label>
                                            </td>
                                            <td width="75%">
                                                <div style={{ width: '100%' }}>
                                                    <RichTextEditor
                                                        value={richTextValue}
                                                        onChange={setRichTextValue}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }

                                if (f.type === 'rolesMultiSelect') {
                                    return (
                                        <tr key={i + 'Tr'} id={f.name + 'Tr'}>
                                            <td width="25%">
                                                <label>{f.label}</label>
                                            </td>
                                            <td width="75%">
                                                <div style={selectWrapperStyle}>
                                                    <Select
                                                        options={ROLES_OPTIONS}
                                                        value={selectedTargetRoles}
                                                        isMulti
                                                        closeMenuOnSelect={false}
                                                        components={animatedComponents}
                                                        placeholder="Select roles..."
                                                        onChange={(selected) => setSelectedTargetRoles(selected || [])}
                                                        styles={selectStylesBase}
                                                        classNamePrefix="form-select"
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        menuPlacement="auto"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }

                                if (f.type === 'select' && f.multiple) {
                                    return (
                                        <tr key={i + 'Tr'} id={f.name + 'Tr'}>
                                            <td width="25%">
                                                <label>
                                                    {f.label}{' '}
                                                    {f.required !== false && <span className="required">*</span>}
                                                </label>
                                            </td>
                                            <td width="75%">
                                                <div style={selectWrapperStyle}>
                                                    <Select
                                                        options={companies}
                                                        value={multipleValues}
                                                        isMulti
                                                        closeMenuOnSelect={false}
                                                        placeholder="Select companies..."
                                                        onChange={(a) => {
                                                            setMultipleValues(a);
                                                            setFieldErrors(prev => ({ ...prev, [f.name]: undefined }));
                                                        }}
                                                        styles={{
                                                            ...selectStylesBase,
                                                            control: (base, state) => ({
                                                                ...formSelectControlFullWidth(base, state),
                                                                ...(fieldErrors[f.name] ? { borderColor: '#e71700', boxShadow: '0 0 0 1px #e71700' } : {}),
                                                            }),
                                                        }}
                                                        classNamePrefix="form-select"
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        menuPlacement="auto"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }

                                if (f.type === 'select') {
                                    return (
                                        <tr key={i + 'Tr'} id={f.name + 'Tr'}>
                                            <td width="25%">
                                                <label>
                                                    {f.label}{' '}
                                                    {f.required !== false && <span className="required">*</span>}
                                                </label>
                                            </td>
                                            <td width="75%">
                                                <FormInput
                                                    name={f.name}
                                                    id={f.name}
                                                    inputType={f.type}
                                                    options={f.options}
                                                    disabled={f.disabled || false}
                                                    menuPlacement="auto"
                                                    onChange={(a) => {
                                                        if (data != null) {
                                                            setData(prev => prev ? { ...prev, [f.name]: a.target.value } : prev);
                                                        }
                                                        if (f.onChanged) f.onChanged(formRef.current, a);
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    );
                                }

                                if (f.type === 'check') {
                                    return (
                                        <tr key={i + 'Tr'} id={f.name + 'Tr'}>
                                            <td width="25%">
                                                <label htmlFor={f.name}>
                                                    {f.label}{' '}
                                                    {f.required !== false && <span className="required">*</span>}
                                                </label>
                                            </td>
                                            <td width="75%">
                                                <input
                                                    type="checkbox"
                                                    id={f.name}
                                                    name={f.name}
                                                    className="form-check-input"
                                                    checked={isActive}
                                                    disabled={f.disabled || false}
                                                    onChange={(e) => setIsActive(e.target.checked)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                }

                                if (id !== undefined && f.editable === false) {
                                    return null;
                                }

                                return (
                                    <tr key={i + 'Tr'} id={f.name + 'Tr'}>
                                        <td width="25%">
                                            <label>
                                                {f.label}{' '}
                                                {f.required !== false && <span className="required">*</span>}
                                            </label>
                                        </td>
                                        <td width="75%">
                                            <FormInput
                                                className="form-control"
                                                name={f.name}
                                                id={f.name}
                                                disabled={f.disabled || false}
                                                inputType={f.type}
                                                value={data?.[f.name]}
                                                onChange={(a) => {
                                                    if (data != null && a?.target != null) {
                                                        setData(prev => prev ? { ...prev, [f.name]: a.target.value } : prev);
                                                    }
                                                }}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="d-flex flex-row mt-2">
                        <CancelButton />
                        <Button type="submit">Save</Button>
                    </div>
                </Form>
            </div>
        </Container>
    );
};

export default AnnualPopupMessageForm;
